import { fetchRemoteConfig } from '@/hotdesk-ui/common-classes/firebase.service';
import { CompareEntryPoint } from '@/hotdesk-ui/enum/compare';
import { RemoteConfigKeys } from '@/hotdesk-ui/enum/remote-keys';
import { isProductionWeb } from '@/hotdesk-ui/helpers/reviewable-booking';
import {
    COMPARE_MAX_ITEMS_LIMIT,
    COMPARE_MIN_ITEMS,
    DEFAULT_COMPARE_CONFIG
} from './constants';
import type { CompareConfig } from './types';

const ENTRY_VALUES = new Set<string>(Object.values(CompareEntryPoint));

/** Remote config allows 2–4; anything else falls back to the default. */
export const normalizeMaxItems = (value: unknown): number => {
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isInteger(n) || n < COMPARE_MIN_ITEMS) {
        return DEFAULT_COMPARE_CONFIG.maxItems;
    }
    return Math.min(n, COMPARE_MAX_ITEMS_LIMIT);
};

export const normalizeEntryPoints = (value: unknown): CompareEntryPoint[] => {
    if (!Array.isArray(value)) return DEFAULT_COMPARE_CONFIG.entryPoints;
    return value.filter(
        (entry): entry is CompareEntryPoint =>
            typeof entry === 'string' && ENTRY_VALUES.has(entry)
    );
};

/**
 * Whether Compare is on before the remote value is known: always on in local
 * and staging, off in production until `web_compare` says so.
 */
export const compareEnabledByDefault = (): boolean => !isProductionWeb();

/**
 * Works on the server and the client, like the other `fetchRemoteConfig` callers.
 * `web_compare` is read in production only (www.hotdesk.com); local and staging
 * always have Compare on. In production a missing or failed value means off.
 */
export const getCompareConfig = async (): Promise<CompareConfig> => {
    const production = isProductionWeb();
    try {
        const [maxItems, entryPoints, flag] = await Promise.all([
            fetchRemoteConfig(RemoteConfigKeys.WEB_COMPARE_MAX_ITEMS, 'number'),
            fetchRemoteConfig(RemoteConfigKeys.WEB_COMPARE_ENTRY_POINTS, 'object'),
            production
                ? fetchRemoteConfig(RemoteConfigKeys.WEB_COMPARE, 'boolean')
                : Promise.resolve(true)
        ]);
        return {
            enabled: flag === true,
            maxItems: normalizeMaxItems(maxItems),
            entryPoints: normalizeEntryPoints(entryPoints)
        };
    } catch {
        return { ...DEFAULT_COMPARE_CONFIG, enabled: !production };
    }
};
